import type { UiNode } from "@sylviq/ui-react-web/shared/extension-schema";
import { ExtensionHost, type CommandInvocation } from "@sylviq/ui-react-web/extensions/ExtensionHost";
import {
  ExtensionViewBlock,
  parseExtensionViewSurface,
  type ExtensionViewSurface,
} from "@sylviq/ui-react-web/blocks/extension";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { useEffect, useRef, useState } from "react";
import {
  ApiError,
  createLocation,
  createLocationLicense,
  createLocationOccupancy,
  createLegalEntity,
  invokeExtensionOperation,
  invokeExtensionV2,
  listExtensionInstallations,
  writeExtensionCredential,
  listLegalEntities,
  listLocationRelationships,
  listLocations,
  listOrganizationLicenses,
  listOrganizations,
} from "@/lib/api";
import { useSession } from "@/lib/session";
import {
  OrganizationNavigation,
  type ConsoleOrganizationOption,
} from "@/features/organizations/OrganizationNavigation";
import {
  applyOrganizationEntityWrites,
  readOrganizationEntityRecords,
} from "@/features/organizations/organizationEntityRecords";
import {
  ORGANIZATION_SETTINGS_SUBMIT,
  organizationSettingsHref,
  organizationSettingsTabs,
  type OrganizationSettingsLegacyTab,
  type OrganizationSettingsTab,
  type OrganizationSettingsV2Tab,
} from "@/features/organizations/organizationSettings";

type V2State =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "loaded"; surface: ExtensionViewSurface; renderToken: string }
  | { status: "error"; message: string };

const HOST_CREDENTIAL_COMMAND = "host.credentials.put.";
const CREDENTIAL_NAME = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const CREDENTIAL_SCOPE_VALUE_BYTES = 128;
const CREDENTIAL_SCOPE_KEYS = 16;

interface HostCredentialSubmission {
  name: string;
  value: string;
  scope: Record<string, string>;
}

function hostCredentialSubmission(invocation: CommandInvocation): HostCredentialSubmission | null {
  if (!invocation.command.startsWith(HOST_CREDENTIAL_COMMAND)) return null;
  const name = invocation.command.slice(HOST_CREDENTIAL_COMMAND.length);
  if (!CREDENTIAL_NAME.test(name)) return null;
  const values = invocation.values ?? {};
  const value = values.value;
  if (typeof value !== "string" || !value || value.includes("\u0000") || new TextEncoder().encode(value).length > 8192) {
    return null;
  }
  const scope: Record<string, string> = {};
  for (const [key, item] of Object.entries(values)) {
    if (key === "value") continue;
    if (!CREDENTIAL_NAME.test(key)) return null;
    if (typeof item !== "string" || !item || item.length > CREDENTIAL_SCOPE_VALUE_BYTES) return null;
    scope[key] = item;
  }
  if (Object.keys(scope).length > CREDENTIAL_SCOPE_KEYS) return null;
  return { name, value, scope };
}

type PageState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | {
      status: "loaded";
      organizationId: string;
      organizationName: string;
      organizations: ConsoleOrganizationOption[];
      tab: OrganizationSettingsTab | null;
    };

export function OrganizationExtensionSettingsPage({
  organizationId,
  contributionId,
}: {
  organizationId: string;
  contributionId: string;
}) {
  const { state } = useSession();
  const panelRef = useRef<HTMLDivElement>(null);
  const saving = useRef(false);
  const retrieving = useRef(false);
  const retrieveAttempt = useRef(0);
  const [page, setPage] = useState<PageState>({ status: "loading" });
  const [notice, setNotice] = useState<{ tone: "saved" | "error"; text: string } | null>(null);
  const [retrieval, setRetrieval] = useState<"retrieving" | "unavailable" | null>(null);
  const [v2, setV2] = useState<V2State>({ status: "idle" });
  const [v2Attempt, setV2Attempt] = useState(0);

  useEffect(() => {
    if (state.status !== "authenticated") return;
    let cancelled = false;
    setPage({ status: "loading" });
    setNotice(null);
    void (async () => {
      try {
        const organizations = await listOrganizations();
        if (cancelled) return;
        const organization = organizations.find((candidate) => candidate.id === organizationId);
        if (!organization) {
          setPage({
            status: "error",
            message: "You do not have access to this organization.",
          });
          return;
        }
        const installations = await listExtensionInstallations(organization.id);
        if (cancelled) return;
        const tab =
          organizationSettingsTabs(installations, organization.id).find(
            (candidate) => candidate.id === contributionId,
          ) ?? null;
        setPage({
          status: "loaded",
          organizationId: organization.id,
          organizationName: organization.name,
          organizations: organizations.map((item) => ({
            id: item.id,
            name: item.name,
            canManageSecrets: item.can_manage_secrets,
          })),
          tab,
        });
      } catch {
        if (!cancelled) {
          setPage({
            status: "error",
            message: "Organization settings could not be loaded. Please try again.",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contributionId, organizationId, state.status]);

  useEffect(() => {
    if (state.status !== "authenticated" || page.status !== "loaded" || page.tab?.format !== "v2") {
      setV2((current) => (current.status === "idle" ? current : { status: "idle" }));
      return;
    }
    const tab = page.tab;
    const organizationId = page.organizationId;
    let cancelled = false;
    setV2({ status: "loading" });
    void (async () => {
      try {
        const response = await invokeExtensionV2(organizationId, tab.extensionInstallationId, {
          kind: "view",
          app: "organizations",
          payload: { viewId: tab.viewId },
        });
        if (cancelled) return;
        if (!response.ok) {
          setV2({
            status: "error",
            message: response.error?.message ?? "This extension view is not available.",
          });
          return;
        }
        const surface = parseExtensionViewSurface(response.result);
        if (!surface) {
          setV2({ status: "error", message: "The extension returned an invalid view." });
          return;
        }
        setV2({ status: "loaded", surface, renderToken: crypto.randomUUID() });
      } catch {
        if (!cancelled) {
          setV2({ status: "error", message: "The extension view could not be loaded." });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, state.status, v2Attempt]);

  function submitV2(tab: OrganizationSettingsV2Tab, invocation: CommandInvocation) {
    if (v2.status !== "loaded" || saving.current) return;
    const values: Record<string, string> = {};
    for (const [name, value] of Object.entries(invocation.values ?? {})) {
      if (typeof value !== "string" || !value) continue;
      if (!/^[A-Za-z][A-Za-z0-9._-]{0,80}$/.test(name)) {
        clearPasswords();
        setNotice({ tone: "error", text: "The settings could not be saved." });
        return;
      }
      if (value.includes("\u0000") || new TextEncoder().encode(value).length > 8192) {
        clearPasswords();
        setNotice({ tone: "error", text: "The settings could not be saved." });
        return;
      }
      values[name] = value;
    }
    clearPasswords();
    if (Object.keys(values).length === 0) {
      setNotice({ tone: "error", text: "Enter the settings before saving." });
      return;
    }
    const organizationId = page.status === "loaded" ? page.organizationId : "";
    if (invocation.command.startsWith("host.")) {
      const host = hostCredentialSubmission(invocation);
      if (!host) {
        setNotice({ tone: "error", text: "The settings could not be saved." });
        return;
      }
      saving.current = true;
      setNotice(null);
      void (async () => {
        try {
          await writeExtensionCredential(
            organizationId,
            tab.extensionInstallationId,
            host.name,
            host.value,
            host.scope,
          );
          setNotice({ tone: "saved", text: "Settings were saved." });
          setV2Attempt((attempt) => attempt + 1);
        } catch (cause) {
          setNotice({ tone: "error", text: saveError(cause) });
        } finally {
          saving.current = false;
          clearPasswords();
        }
      })();
      return;
    }
    const facilities = invocation.command === "niche.metrc.facilities";
    saving.current = true;
    setNotice(null);
    void (async () => {
      try {
        const response = await invokeExtensionV2(organizationId, tab.extensionInstallationId, {
          kind: "ui_command",
          app: "organizations",
          payload: {
            command: invocation.command,
            values,
            viewId: tab.viewId,
            selectedIds: [],
            renderToken: v2.renderToken,
          },
        });
        if (!response.ok) {
          if (facilities) {
            setNotice({
              tone: "error",
              text: "Facilities were not retrieved. Check the API key.",
            });
            return;
          }
          throw new Error(response.error?.message ?? "extension command failed");
        }
        if (facilities) {
          const result = response.result;
          const prepared = result && typeof result === "object" && "prepared_response" in result
            ? (result as { prepared_response?: { status_code?: unknown; body?: unknown } }).prepared_response
            : undefined;
          const statusCode = prepared?.status_code;
          if (
            typeof statusCode === "number"
            && statusCode >= 200
            && statusCode < 300
            && await applyEntityRecords(organizationId, prepared?.body) === "saved"
          ) {
            setNotice({ tone: "saved", text: "Entities and locations were saved." });
            return;
          }
          setNotice({
            tone: "error",
            text: "Facilities were not retrieved. Check the API key.",
          });
          return;
        }
        setNotice({ tone: "saved", text: "Settings were saved." });
        setV2Attempt((attempt) => attempt + 1);
      } catch {
        setNotice({
          tone: "error",
          text: facilities
            ? "Facilities were not retrieved. Check the API key."
            : "The settings could not be saved.",
        });
      } finally {
        saving.current = false;
        clearPasswords();
      }
    })();
  }

  function clearPasswords() {
    panelRef.current?.querySelectorAll("input[type=password]").forEach((node) => {
      if (node instanceof HTMLInputElement) node.value = "";
    });
  }

  async function applyEntityRecords(
    organizationId: string,
    body: unknown,
  ): Promise<"saved" | "absent" | "failed"> {
    const records = readOrganizationEntityRecords(body);
    if (records.status === "absent") return "absent";
    if (records.status !== "records") return "failed";
    const [entities, locations, licenses] = await Promise.all([
      listLegalEntities(organizationId),
      listLocations(organizationId),
      listOrganizationLicenses(organizationId),
    ]);
    const applied = await applyOrganizationEntityWrites(
      records.entities,
      {
        entities: entities.map((entity) => ({
          id: entity.id,
          name: entity.name,
          slug: entity.slug,
        })),
        locations: locations.map((location) => ({
          id: location.id,
          code: location.code,
        })),
        licenses: licenses.map((license) => ({
          id: license.id,
          legalEntityId: license.legal_entity_id,
          locationId: license.location_id,
          licenseNumber: license.license_number,
        })),
      },
      {
        createEntity: (entity) => createLegalEntity({
          organization_id: organizationId,
          name: entity.name,
          slug: entity.slug,
          kind: "organization",
        }),
        createLocation: (location) => createLocation({
          organization_id: organizationId,
          code: location.code,
          name: location.name,
          address_line1: location.addressLine1,
          address_line2: location.addressLine2,
          city: location.city,
          region: location.region,
          postal_code: location.postalCode,
          country_code: location.countryCode,
        }),
        listRelationships: async (locationId) => (
          await listLocationRelationships(organizationId, locationId)
        ).map((relationship) => ({
          legalEntityId: relationship.legal_entity_id,
          kind: relationship.kind,
        })),
        createOccupancy: (occupancy) => createLocationOccupancy({
          organization_id: organizationId,
          location_id: occupancy.locationId,
          legal_entity_id: occupancy.legalEntityId,
        }).then(() => undefined),
        createLicense: (license) => createLocationLicense(license.locationId, {
          organization_id: organizationId,
          legal_entity_id: license.legalEntityId,
          regulator: license.regulator,
          jurisdiction: license.jurisdiction,
          license_number: license.licenseNumber,
          license_type: license.licenseType,
          status: "active",
        }),
      },
    );
    return applied.ok ? "saved" : "failed";
  }

  function operationInput(): Record<string, string> | null {
    const fields = panelRef.current?.querySelectorAll("input[name], textarea[name]");
    if (!fields) return null;
    const input: Record<string, string> = {};
    for (const node of fields) {
      if (!(node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement)) continue;
      if (node instanceof HTMLInputElement && node.type === "password") continue;
      if (!/^[A-Za-z][A-Za-z0-9._-]{0,80}$/.test(node.name)) return null;
      const value = node.value.trim();
      if (!value || value.includes("\u0000") || value.length > 64) return null;
      input[node.name] = value;
    }
    return Object.keys(input).length > 0 ? input : null;
  }

  function retrieve(tab: OrganizationSettingsLegacyTab) {
    if (
      retrieving.current
      || saving.current
      || state.status !== "authenticated"
      || !tab.retrieveOperation
    ) {
      return;
    }
    const installationId = tab.extensionInstallationId;
    const input = operationInput();
    if (!installationId || !input) {
      setRetrieval("unavailable");
      return;
    }
    const attempt = retrieveAttempt.current + 1;
    retrieveAttempt.current = attempt;
    retrieving.current = true;
    setNotice(null);
    setRetrieval("retrieving");
    const organizationId = page.status === "loaded" ? page.organizationId : "";
    const operation = tab.retrieveOperation;
    void (async () => {
      let outcome: "retrieving" | "unavailable" | null = "unavailable";
      let saved = false;
      try {
        const result = await invokeExtensionOperation(
          organizationId,
          installationId,
          operation,
          input,
        );
        const statusCode = result.prepared_response?.status_code;
        if (
          result.status === "succeeded"
          && typeof statusCode === "number"
          && statusCode >= 200
          && statusCode < 300
        ) {
          const applied = await applyEntityRecords(
            organizationId,
            result.prepared_response?.body,
          );
          if (applied === "absent") outcome = null;
          if (applied === "saved") {
            outcome = null;
            saved = true;
          }
        }
      } catch {
        outcome = "unavailable";
      } finally {
        retrieving.current = false;
        if (retrieveAttempt.current === attempt) {
          setRetrieval(outcome);
          if (saved && outcome === null) {
            setNotice({ tone: "saved", text: "Entities and locations were saved." });
          }
        }
      }
    })();
  }

  function onCommand(invocation: CommandInvocation) {
    if (page.status !== "loaded" || !page.tab || state.status !== "authenticated") return;
    if (page.tab.format === "v2") {
      submitV2(page.tab, invocation);
      return;
    }
    if (
      page.tab.retrieveOperation
      && invocation.command === `organizations.settings.retrieve.${page.tab.retrieveOperation}`
    ) {
      retrieve(page.tab);
      return;
    }
    if (
      saving.current
      || retrieving.current
      || invocation.command !== ORGANIZATION_SETTINGS_SUBMIT
    ) {
      return;
    }
    const tab = page.tab;
    const values: Record<string, string> = {};
    for (const [name, value] of Object.entries(invocation.values ?? {})) {
      if (typeof value !== "string" || !value) continue;
      if (!/^[A-Za-z][A-Za-z0-9._-]{0,80}$/.test(name)) {
        clearPasswords();
        setNotice({ tone: "error", text: "The settings could not be saved." });
        return;
      }
      if (value.includes("\u0000") || new TextEncoder().encode(value).length > 8192) {
        clearPasswords();
        setNotice({ tone: "error", text: "The settings could not be saved." });
        return;
      }
      values[name] = value;
    }
    clearPasswords();
    if (Object.keys(values).length === 0) {
      setNotice({ tone: "error", text: "Enter the settings before saving." });
      return;
    }
    const installationId = tab.extensionInstallationId;
    if (!installationId) {
      setNotice({
        tone: "error",
        text: "This settings tab has no extension to store the values.",
      });
      return;
    }
    const organizationId = page.organizationId;
    saving.current = true;
    setNotice(null);
    void (async () => {
      try {
        const result = await invokeExtensionOperation(
          organizationId,
          installationId,
          "organizations.settings.store",
          values,
        );
        if (result.status !== "succeeded") throw new Error("settings not saved");
        setNotice({ tone: "saved", text: "Settings were saved." });
      } catch (cause) {
        setNotice({ tone: "error", text: saveError(cause) });
      } finally {
        saving.current = false;
        clearPasswords();
      }
    })();
  }

  if (state.status !== "authenticated" || page.status === "loading") {
    return (
      <main className="mx-auto max-w-5xl px-6 py-10">
        <p className="text-sm text-[var(--muted-foreground)]">Loading organization settings...</p>
      </main>
    );
  }

  if (page.status === "error") {
    return (
      <main className="mx-auto max-w-5xl px-6 py-10">
        <p role="alert" className="text-sm text-[var(--danger)]">
          {page.message}
        </p>
      </main>
    );
  }

  const legacyTab = page.tab?.format === "v1" ? page.tab : null;

  return (
    <OrganizationNavigation
      organizationId={page.organizationId}
      organizations={page.organizations}
      onOrganizationChange={(nextOrganizationId) => {
        if (nextOrganizationId === page.organizationId) return;
        window.location.assign(organizationSettingsHref(nextOrganizationId, contributionId));
      }}
      view="settings"
      activeSettingsId={page.tab?.id}
    >
      <div ref={panelRef} className="mx-auto max-w-3xl px-6 py-10">
        {page.tab ? (
          page.tab.format === "v2" ? (
            <div>
              {v2.status === "loading" ? (
                <p className="text-sm text-[var(--muted-foreground)]">Loading extension view...</p>
              ) : null}
              {v2.status === "error" ? (
                <p role="alert" className="text-sm text-[var(--danger)]">{v2.message}</p>
              ) : null}
              {v2.status === "loaded" ? (
                <ExtensionViewBlock surface={v2.surface} onCommand={onCommand} />
              ) : null}
            </div>
          ) : (
            <ExtensionHost node={page.tab.view as UiNode} onCommand={onCommand} />
          )
        ) : (
          <p role="alert" className="text-sm text-[var(--danger)]">
            This settings tab is not available.
          </p>
        )}
        <Dialog
          open={retrieval !== null && legacyTab !== null}
          onOpenChange={(open) => {
            if (!open) {
              retrieveAttempt.current += 1;
              retrieving.current = false;
              setRetrieval(null);
            }
          }}
          title={page.tab?.label ?? "Organization settings"}
          description={legacyTab && retrieval
            ? retrieval === "retrieving" ? legacyTab.retrieving : legacyTab.unavailable
            : undefined}
        >
          {legacyTab && retrieval === "unavailable" ? (
            <p role="alert" className="sr-only">{legacyTab.unavailable}</p>
          ) : null}
        </Dialog>
        {notice ? (
          <p
            role={notice.tone === "error" ? "alert" : "status"}
            className={`mt-4 text-sm ${notice.tone === "error" ? "text-[var(--danger)]" : "text-[var(--muted-foreground)]"}`}
          >
            {notice.text}
          </p>
        ) : null}
      </div>
    </OrganizationNavigation>
  );
}

function saveError(cause: unknown): string {
  if (cause instanceof ApiError && cause.status === 403) {
    return "Only the organization owner can save these settings.";
  }
  return "The settings could not be saved.";
}
